import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import SystemStatusBar from '../components/testpool/SystemStatusBar';
import StatsCards from '../components/testpool/StatsCards';
import WorkerCard from '../components/testpool/WorkerCard';
import ControlCenter from '../components/testpool/ControlCenter';
import PasswordArsenal from '../components/testpool/PasswordArsenal';
import QuickTools from '../components/testpool/QuickTools';
import LiveFeed from '../components/testpool/LiveFeed';
import DangerZone from '../components/testpool/DangerZone';

const API_URL = process.env.REACT_APP_API_URL || 'https://pose.quest/api';

export default function TestPool() {
  const [stats, setStats] = useState({ total: 0, pending: 0, processing: 0, success: 0, failed: 0 });
  const [speed, setSpeed] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [workerCount, setWorkerCount] = useState(150);
  const [globalPasswords, setGlobalPasswords] = useState(['258080', '235689']);
  const [newPassword, setNewPassword] = useState('');
  const [cleaning, setCleaning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetProgress, setResetProgress] = useState(0);
  const [showResetModal, setShowResetModal] = useState(false);
  const [liveFeed, setLiveFeed] = useState([]);
  const feedRef = useRef(null);
  const [systemHealth, setSystemHealth] = useState({ cpu: 0, memory: 0, disk: 0, chrome: 0, load: 0 });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [proxyEnabled, setProxyEnabled] = useState(true);
  const [pingResult, setPingResult] = useState(null);
  const [proxyStatus, setProxyStatus] = useState(null);
  const [scrapers, setScrapers] = useState([]);
  const [activeScraper, setActiveScraper] = useState('garanti');
  const [scraperDropdownOpen, setScraperDropdownOpen] = useState(false);
  const [celeryWorkers, setCeleryWorkers] = useState([]);
  const [remoteWorkers, setRemoteWorkers] = useState([]);
  const [workerActionLoading, setWorkerActionLoading] = useState(null);

  useEffect(() => {
    loadStats(); loadSpeed(); loadGlobalPasswords(); loadProxyToggle(); loadSystemHealth(); loadScrapers(); loadWorkers(); checkRunning();
    const interval = setInterval(() => { loadStats(); loadSystemHealth(); checkRunning(); loadSpeed(); }, 5000);
    const workerInterval = setInterval(() => { loadWorkers(); }, 15000);
    return () => { clearInterval(interval); clearInterval(workerInterval); };
  }, []);

  useEffect(() => {
    if (!isRunning) return;
    const feedInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API_URL}/logs/live`);
        if (response.data.logs) setLiveFeed(prev => [...response.data.logs.slice(-50), ...prev].slice(0, 100));
      } catch (err) { console.error('Feed error:', err); }
    }, 3000);
    return () => clearInterval(feedInterval);
  }, [isRunning]);

  useEffect(() => { if (feedRef.current) feedRef.current.scrollTop = 0; }, [liveFeed.length]);

  const loadStats = async () => { try { const r = await axios.get(`${API_URL}/pool/stats-bulk`); setStats(r.data); } catch (e) {} };
  const loadSpeed = async () => { try { const r = await axios.get(`${API_URL}/pool/speed`); if (r.data.success) setSpeed(r.data.speed); } catch (e) {} };
  const loadSystemHealth = async () => { try { const r = await axios.get(`${API_URL}/dashboard/system-health`); if (r.data.success) setSystemHealth(r.data.data); } catch (e) {} };
  const checkRunning = async () => { try { const r = await axios.get(`${API_URL}/pool/running`); setIsRunning(r.data.running); } catch (e) {} };
  const loadGlobalPasswords = async () => { try { const r = await axios.get(`${API_URL}/passwords/get`); if (r.data.success) setGlobalPasswords(r.data.passwords); } catch (e) {} };
  const loadProxyToggle = async () => { try { const r = await axios.get(`${API_URL}/settings/proxy/toggle`); setProxyEnabled(r.data.enabled); } catch (e) {} };

  const loadScrapers = async () => {
    try {
      const r = await axios.get(`${API_URL}/pool/scrapers`);
      if (r.data.success) { setScrapers(r.data.scrapers); const a = r.data.scrapers.find(s => s.active); if (a) setActiveScraper(a.id); }
    } catch (e) {}
  };

  const loadWorkers = async () => {
    try {
      const [celeryRes, remoteRes] = await Promise.all([
        axios.get(`${API_URL}/pool/workers`),
        axios.get(`${API_URL}/pool/workers/remote-stats`).catch(() => ({ data: { workers: [] } }))
      ]);
      if (celeryRes.data.success) setCeleryWorkers(celeryRes.data.workers || []);
      if (remoteRes.data.success) setRemoteWorkers(remoteRes.data.workers || []);
    } catch (e) {}
  };

  const handleWorkerAction = async (host, action) => {
    setWorkerActionLoading(`${host}-${action}`);
    try {
      let r;
      if (action === 'ping') {
        r = await axios.post(`${API_URL}/pool/workers/ping`, { host });
        if (r.data.success) {
          setMessage({ type: r.data.status === 'OK' ? 'success' : 'error', text: `${r.data.host}: HTTP ${r.data.http_code} - ${r.data.status}` });
          setWorkerActionLoading(null);
          setTimeout(() => setMessage({ type: '', text: '' }), 5000);
          return;
        }
      }
      r = await axios.post(`${API_URL}/pool/workers/control`, { host, action });
      if (r.data.success) { setMessage({ type: 'success', text: `${r.data.worker}: ${action} OK` }); setTimeout(() => loadWorkers(), 2000); }
      else { setMessage({ type: 'error', text: `${action} failed` }); }
    } catch (e) { setMessage({ type: 'error', text: `${action} error` }); }
    setWorkerActionLoading(null);
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleScraperChange = async (scraperId) => {
    if (isRunning) { setMessage({ type: 'error', text: '⚠️ Queue durdurulmalı!' }); setTimeout(() => setMessage({ type: '', text: '' }), 3000); return; }
    try {
      const r = await axios.post(`${API_URL}/pool/scrapers/set`, { scraper_id: scraperId });
      if (r.data.success) { setActiveScraper(scraperId); setScrapers(r.data.scrapers); const s = r.data.scrapers.find(x => x.id === scraperId); setMessage({ type: 'success', text: `✅ Scraper: ${s?.name || scraperId}` }); setTimeout(() => setMessage({ type: '', text: '' }), 3000); }
    } catch (e) { setMessage({ type: 'error', text: '❌ Scraper değiştirilemedi!' }); }
    setScraperDropdownOpen(false);
  };

  const handleStart = async () => { try { const r = await axios.post(`${API_URL}/pool/start-bulk`, { limit: workerCount }); setMessage({ type: 'success', text: `🚀 ${r.data.started} tests started!` }); setIsRunning(true); setTimeout(() => setMessage({ type: '', text: '' }), 3000); } catch (e) { setMessage({ type: 'error', text: '❌ Start failed!' }); } };
  const handlePause = async () => { try { await axios.post(`${API_URL}/pool/pause`); setIsRunning(false); setMessage({ type: 'info', text: '⏸️ Queue paused' }); setTimeout(() => setMessage({ type: '', text: '' }), 3000); } catch (e) { setMessage({ type: 'error', text: '❌ Pause failed!' }); } };
  const handleSetWorkers = async (count) => { setWorkerCount(count); try { await axios.post(`${API_URL}/pool/worker-count`, { count }); } catch (e) {} };
  const handleGarantiPing = async () => { try { const r = await axios.get(`${API_URL}/pool/garanti-ping`); setPingResult(r.data); setTimeout(() => setPingResult(null), 5000); } catch (e) { setPingResult({ status: 'ERROR' }); } };
  const handleProxyRefresh = async () => { try { const r = await axios.post(`${API_URL}/pool/proxy-refresh`); setProxyStatus(r.data); setTimeout(() => setProxyStatus(null), 5000); } catch (e) { setProxyStatus({ success: false }); } };
  const handleProxyToggle = async () => { try { const ns = !proxyEnabled; await axios.post(`${API_URL}/settings/proxy/toggle`, { enabled: ns }); setProxyEnabled(ns); setMessage({ type: 'success', text: `Proxy ${ns ? 'ON' : 'OFF'}` }); setTimeout(() => setMessage({ type: '', text: '' }), 3000); } catch (e) { setMessage({ type: 'error', text: 'Proxy toggle error' }); } };
  const handleUpdatePasswords = async () => { try { const r = await axios.post(`${API_URL}/passwords/update`, { passwords: globalPasswords }); if (r.data.success) { setMessage({ type: 'success', text: '✅ Passwords updated!' }); setTimeout(() => setMessage({ type: '', text: '' }), 3000); } } catch (e) { setMessage({ type: 'error', text: '❌ Update failed!' }); } };
  const handleAddPassword = () => { if (newPassword.trim() && !globalPasswords.includes(newPassword.trim())) { setGlobalPasswords([...globalPasswords, newPassword.trim()]); setNewPassword(''); } };
  const handleRemovePassword = (index) => { if (globalPasswords.length > 1) setGlobalPasswords(globalPasswords.filter((_, i) => i !== index)); };

  const handleExportHits = async () => {
    try { setExporting(true); const r = await axios.get(`${API_URL}/pool/export-hits`, { responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([r.data])); const link = document.createElement('a'); link.href = url; link.setAttribute('download', `hits_${Date.now()}.xlsx`); document.body.appendChild(link); link.click(); link.remove(); setMessage({ type: 'success', text: '✅ Export successful!' }); } catch (e) { setMessage({ type: 'error', text: '❌ Export failed!' }); } finally { setExporting(false); }
  };

  const handleEmergencyReset = async () => {
    setShowResetModal(false); setIsResetting(true); setResetProgress(0);
    const pi = setInterval(() => { setResetProgress(prev => prev >= 90 ? 90 : prev + 10); }, 400);
    try {
      const r = await axios.post(`${API_URL}/pool/emergency-reset`);
      clearInterval(pi); setResetProgress(100);
      setTimeout(() => { setIsResetting(false); setResetProgress(0); setMessage({ type: 'success', text: `✅ System reset! ${r.data.workers || 'OK'} workers` }); loadStats(); loadSystemHealth(); }, 500);
    } catch (e) { clearInterval(pi); setIsResetting(false); setResetProgress(0); setMessage({ type: 'error', text: '❌ Reset failed!' }); }
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const formatNumber = (num) => new Intl.NumberFormat('en-US').format(num);
  const successRate = stats.success + stats.failed > 0 ? ((stats.success / (stats.success + stats.failed)) * 100).toFixed(1) : 0;
  const activeScraperInfo = scrapers.find(s => s.id === activeScraper);

  return (
    <div className="space-y-6 text-white">
      <DangerZone cleaning={cleaning} isResetting={isResetting} resetProgress={resetProgress}
        showResetModal={showResetModal} onShowResetModal={() => setShowResetModal(true)}
        onHideResetModal={() => setShowResetModal(false)} onEmergencyReset={handleEmergencyReset} />

      <SystemStatusBar systemHealth={systemHealth} isRunning={isRunning} speed={speed}
        successRate={successRate} activeScraperInfo={activeScraperInfo} celeryWorkers={celeryWorkers} />

      {message.text && (
        <div className={`mb-4 px-6 py-3 rounded-xl font-bold text-center ${
          message.type === 'success' ? 'bg-green-600/20 text-green-400 border border-green-500/50' :
          message.type === 'error' ? 'bg-red-600/20 text-red-400 border border-red-500/50' :
          'bg-blue-600/20 text-blue-400 border border-blue-500/50'
        }`}>{message.text}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        <div className="lg:col-span-8 space-y-4">
          <StatsCards stats={stats} formatNumber={formatNumber} onExportHits={handleExportHits} exporting={exporting} />
          <WorkerCard celeryWorkers={celeryWorkers} remoteWorkers={remoteWorkers}
            workerActionLoading={workerActionLoading} onWorkerAction={handleWorkerAction}
            onRefresh={loadWorkers} formatNumber={formatNumber} />
          <PasswordArsenal globalPasswords={globalPasswords} newPassword={newPassword}
            onNewPasswordChange={setNewPassword} onAddPassword={handleAddPassword}
            onRemovePassword={handleRemovePassword} onUpdatePasswords={handleUpdatePasswords} />
          <QuickTools onGarantiPing={handleGarantiPing} onProxyRefresh={handleProxyRefresh}
            pingResult={pingResult} proxyStatus={proxyStatus} />
          <ControlCenter isRunning={isRunning} workerCount={workerCount} proxyEnabled={proxyEnabled}
            activeScraperInfo={activeScraperInfo} activeScraper={activeScraper} scrapers={scrapers}
            scraperDropdownOpen={scraperDropdownOpen}
            onScraperDropdownToggle={() => setScraperDropdownOpen(!scraperDropdownOpen)}
            onScraperChange={handleScraperChange} onProxyToggle={handleProxyToggle}
            onSetWorkers={handleSetWorkers} onStart={handleStart} onPause={handlePause} />
        </div>
        <div className="lg:col-span-4">
          <LiveFeed liveFeed={liveFeed} isRunning={isRunning} feedRef={feedRef} />
        </div>
      </div>
    </div>
  );
}
